import { useCallback } from "react";

import { ROUTES } from "@/constants/routes";

import type { CreateWatchFormDefaults } from "../router-states/watchlist/create-watch";

import { getCreateWatchRouterStateFromPathname } from "../router-states/watchlist/create-watch";
import { useNavigateWithRouterState } from "./useNavigateWithRouterState";

/**
 * Navigate to `/watchlist/create`, optionally attaching prefilled form defaults
 * and a return `origin` when creating a new watch from an existing watch
 * (e.g. description edit flow).
 *
 * Prefer `getLinkProps` for `Button`/`AriaLink` with `href` so react-aria's
 * `RouterProvider` receives the same `state` (plain `onClick` is bypassed).
 */
export function useNavigateToCreateWatch() {
	const { getLinkProps: getLinkPropsInternal, navigate: navigateInternal } =
		useNavigateWithRouterState({
			buildState: buildCreateWatchState,
			buildTo: buildCreateWatchTo,
		});

	// Optional arg wrappers — `useNavigateWithRouterState` requires a TArg slot.
	const navigate = useCallback(
		(defaults?: CreateWatchFormDefaults) => navigateInternal(defaults),
		[navigateInternal],
	);

	const getLinkProps = useCallback(
		(defaults?: CreateWatchFormDefaults) => getLinkPropsInternal(defaults),
		[getLinkPropsInternal],
	);

	return { getLinkProps, navigate };
}

function buildCreateWatchState(
	pathname: string,
	defaults?: CreateWatchFormDefaults,
) {
	return getCreateWatchRouterStateFromPathname(pathname, defaults);
}

function buildCreateWatchTo(_defaults?: CreateWatchFormDefaults) {
	return ROUTES.WATCHLIST_CREATE;
}
